import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import {
    Box,
    Button,
    Chip,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { type MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    useEditAssetMutation,
    useGetAllReservationsQuery,
    useGetIndividualOwnerAssetsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { selectExperienceFilters } from '../../../../app/Slices/ExperienceFiltersSlice';
import {
    selectEndDate,
    selectStartDate,
} from '../../../../app/Slices/DateFilterSlice';
import { selectExperienceSearchFilter } from '../../../../app/Slices/ExperienceSearchSlice';
import { useGetAllPublicHolidaysForYearAndCountryQuery } from '../../../../app/Slices/PublicHolidaysSlice';
import OwnerListingsTableLoadingSkeleton from './OwnerListingsTableLoadingSkeleton';
import OwnerListingMobileCard from './OwnerListingMobileCard';
import DeleteExperienceModal from './DeleteExperienceModal';
import UserRole from '../../../../shared/AuthRoles';
import {
    capitalizeName,
    filterItemsBySearch,
    formatCurrency,
} from '../../../../shared/utils';
import GenericTable from '../../../shared/GenericTable';
import toast from '../../../shared/Toast';
import { filterItemsByDateRange } from '../ExperiencesListAllComponent';
import type { OwnerListingStatusFilter } from './OwnerListingsHero';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { alpha } from '@mui/material';
import { Experience } from '../../../types/experience';
import { formatHireRange as formatExperienceHireRange } from '../IndividualExperienceDetails';

const formatUnitType = (unitType: string) => {
    const typeMap: Record<string, string> = {
        fixed: 'fixed price',
        perHour: 'per hour',
        perDay: 'per day',
        perNight: 'per night',
        perHourPerGuest: 'per hour per guest',
        perDayPerGuest: 'per day per guest',
        perNightPerGuest: 'per night per guest',
        perDeskPerDay: 'per day',
        perOfficePerDay: 'per day',
        perMeetingRoomPerDay: 'per day',
        entireVenuePerDay: 'per day',
        entireVenuePerNight: 'per night',
    };

    return typeMap[unitType] || unitType;
};

const getPriceRange = (experience: any) => {
    if (experience?.billingExcluded) {
        return {
            minPrice: 0,
            maxPrice: 0,
            unitType: null,
            billingExcluded: true,
            isFlexible: false,
        };
    }

    const charges = experience?.charges || [];
    const today = new Date();

    const hasFlexiblePrice = charges.some(
        (p: any) => p.chargeType === 'flexiblePrice',
    );

    if (hasFlexiblePrice) {
        return {
            minPrice: 0,
            maxPrice: 0,
            unitType: null,
            billingExcluded: false,
            isFlexible: true,
        };
    }

    const activeDiscount = experience?.discounts?.find(
        (discount: any) =>
            !discount.disabled &&
            !discount.expired &&
            Array.isArray(discount.dates) &&
            discount.dates.some((range: any) => {
                const start = new Date(range.startDate);
                const end = new Date(range.endDate);
                return today >= start && today <= end;
            }),
    );

    const discountPercentage = activeDiscount?.percentage ?? 0;
    const priceCharges = charges.filter(
        (p: any) => p.chargeType === 'price' && typeof p.unitPrice === 'number',
    );

    if (priceCharges.length === 0) {
        return {
            minPrice: 0,
            maxPrice: 0,
            unitType: null,
            billingExcluded: false,
            isFlexible: false,
        };
    }

    const sorted = [...priceCharges].sort((a, b) => a.unitPrice - b.unitPrice);
    const min = sorted[0];
    const minPrice = min.unitPrice * (1 - discountPercentage / 100);

    return {
        minPrice: Math.round(minPrice),
        unitType: min.type,
        billingExcluded: false,
        isFlexible: false,
    };
};

const getListingSubType = (experience: any) => {
    if (
        experience?.type === 'commercial' &&
        experience?.details?.subType === 'house'
    ) {
        return 'Home office';
    }
    return capitalizeName(experience?.details?.subType ?? experience?.type);
};

const getExperienceState = (experience: any) =>
    experience?.state?.trim()
        ? capitalizeName(String(experience.state).trim())
        : '';

const getLocationLabel = (experience: any) =>
    [experience?.suburb, experience?.state, experience?.countryCode]
        .filter(Boolean)
        .map((part) => capitalizeName(String(part)))
        .join(', ');

const isUnpublishedListing = (experience: any) =>
    experience?.isAvailable === false;

const getRowTextColor = (experience: any) =>
    isUnpublishedListing(experience) ? 'text.secondary' : 'text.primary';

const formatHireRange = (experience: Experience) => {
    if (experience.status === 'draft') {
        return '';
    }

    return formatExperienceHireRange(experience);
};

const formatBookedRange = (startDate: string, endDate: string) => {
    const start = moment(startDate);
    const end = moment(endDate);
    const startLabel = start.isSame(moment(), 'day')
        ? 'Today'
        : start.format('D MMM YYYY');
    return `${startLabel} - ${end.format('D MMM YYYY')}`;
};

type OwnerListingsTableProps = {
    visitType?: string;
    statusFilter: OwnerListingStatusFilter;
    locationFilter: string;
    onExperiencesLoaded?: (experiences: any[]) => void;
};

export default function ExperiencesListOwnerComponent({
    visitType = '',
    statusFilter,
    locationFilter,
    onExperiencesLoaded,
}: OwnerListingsTableProps) {
    const role = UserRole();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const experienceFilters = useSelector(selectExperienceFilters);
    const startDate = useSelector(selectStartDate);
    const endDate = useSelector(selectEndDate);
    const searchTerm = useSelector(selectExperienceSearchFilter);
    const [editAsset] = useEditAssetMutation();
    const [unpublishId, setUnpublishId] = useState<number | null>(null);
    const [experienceToDelete, setExperienceToDelete] =
        useState<Experience | null>(null);

    const { data: experienceList, isFetching } =
        useGetIndividualOwnerAssetsQuery({
            url: `owners/${role?.company}/modules`,
            ...experienceFilters,
            ...(visitType ? { visitType } : {}),
            sort: 'name',
        });

    const { data: upcomingBookings } = useGetAllReservationsQuery({
        status: ['approved'],
        sort: 'startDate',
    });

    const { data: publicHolidaysAustraliaCurrentYear } =
        useGetAllPublicHolidaysForYearAndCountryQuery({
            country: 'AU',
            year: new Date().getFullYear(),
        });
    const { data: publicHolidaysAustraliaNextYear } =
        useGetAllPublicHolidaysForYearAndCountryQuery({
            country: 'AU',
            year: new Date().getFullYear() + 1,
        });
    const { data: publicHolidaysAustraliaYearAfterNextYear } =
        useGetAllPublicHolidaysForYearAndCountryQuery({
            country: 'AU',
            year: new Date().getFullYear() + 2,
        });

    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!isFetching) {
            const delay = setTimeout(() => setIsLoading(false), 500);
            return () => clearTimeout(delay);
        }
        setIsLoading(true);
    }, [isFetching]);

    useEffect(() => {
        if (experienceList) {
            onExperiencesLoaded?.(experienceList);
        }
    }, [experienceList, onExperiencesLoaded]);

    const publicHolidaysAustralia =
        publicHolidaysAustraliaCurrentYear &&
        publicHolidaysAustraliaNextYear &&
        publicHolidaysAustraliaYearAfterNextYear
            ? [
                  ...publicHolidaysAustraliaCurrentYear,
                  ...publicHolidaysAustraliaNextYear,
                  ...publicHolidaysAustraliaYearAfterNextYear,
              ]
            : [];

    const allAssets =
        experienceList && publicHolidaysAustralia
            ? filterItemsByDateRange(
                  experienceList,
                  startDate,
                  endDate,
                  publicHolidaysAustralia,
              )
            : undefined;

    const filteredAssets = useMemo(() => {
        let items = allAssets ? filterItemsBySearch(allAssets, searchTerm) : [];

        if (statusFilter === 'draft') {
            items = items.filter(
                (experience: any) => experience.status === 'draft',
            );
        } else if (statusFilter === 'published') {
            items = items.filter(
                (experience: any) =>
                    experience.status !== 'draft' && experience.isAvailable,
            );
        } else if (statusFilter === 'unpublished') {
            items = items.filter(
                (experience: any) =>
                    experience.status !== 'draft' && !experience.isAvailable,
            );
        }

        if (locationFilter !== 'all') {
            items = items.filter(
                (experience: any) =>
                    getExperienceState(experience) === locationFilter,
            );
        }

        return items;
    }, [allAssets, searchTerm, statusFilter, locationFilter]);

    const bookingByModuleId = useMemo(() => {
        const map = new Map<number, { startDate: string; endDate: string }>();
        const today = moment().startOf('day');

        (upcomingBookings ?? []).forEach((booking: any) => {
            const moduleId = booking?.module?.id ?? booking?.moduleId;
            if (!moduleId || map.has(moduleId)) return;

            const end = moment(booking.endDate);
            if (end.isBefore(today, 'day')) return;

            map.set(moduleId, {
                startDate: booking.startDate,
                endDate: booking.endDate,
            });
        });

        return map;
    }, [upcomingBookings]);

    const handleOpen = (experience: any) => {
        if (experience.status === 'draft') {
            navigate(`/experiences/new/${experience.id}`);
            return;
        }

        navigate(
            `/viewExperiences/${role?.company}/${experience?.type?.toLowerCase()}/${experience?.id}`,
            {
                state: { experience, component: 'all' },
            },
        );
    };

    const handleEdit = (experience: any) => {
        if (experience.status === 'published') {
            navigate(`/experiences/edit/${experience.id}`);
        } else {
            navigate(`/experiences/new/${experience.id}`);
        }
    };

    const handleUnpublish = async (experience: any) => {
        if (!role?.company) return;

        try {
            setUnpublishId(experience.id);
            await editAsset([
                role.company,
                experience.id,
                { type: experience.type, isAvailable: false },
            ]).unwrap();
            toast.success(`${experience.name} has been unpublished`);
        } catch {
            toast.error(`Failed to unpublish ${experience.name}`);
        } finally {
            setUnpublishId(null);
        }
    };

    const columns = useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                id: 'listing',
                header: 'Listing',
                size: 360,
                grow: 1,
                accessorFn: (row) => row.name,
                Cell: ({ row }) => {
                    const experience = row.original;
                    const photoSrc = experience?.photos?.[0]?.src;
                    const isUnpublished = isUnpublishedListing(experience);
                    const nameColor = getRowTextColor(experience);
                    const detailColor = 'text.secondary';

                    return (
                        <Stack
                            direction='row'
                            spacing={2}
                            alignItems='center'
                            sx={{ my: 0.5 }}
                        >
                            <Box sx={{ position: 'relative' }}>
                                <Box
                                    component='img'
                                    src={photoSrc}
                                    alt=''
                                    sx={{
                                        width: 117,
                                        height: 88,
                                        objectFit: 'cover',
                                        borderRadius: 2,
                                        flexShrink: 0,
                                        bgcolor: 'action.hover',
                                        opacity: isUnpublished ? 0.55 : 1,
                                    }}
                                />

                                {!experience.isAvailable && (
                                    <Box
                                        sx={(theme) => ({
                                            position: 'absolute',
                                            top: '0',
                                            left: '0',
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            borderRadius: 2,
                                            backgroundColor: `${alpha(
                                                theme.palette.black.main,
                                                0.6,
                                            )}`,
                                        })}
                                    >
                                        <VisibilityOffIcon
                                            sx={{
                                                fontSize: 40,
                                                color: 'neutral.main',
                                                opacity: 0.6,
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>

                            <Stack spacing={0} sx={{ minWidth: 0 }}>
                                <Typography
                                    variant='body2'
                                    fontWeight={700}
                                    color={nameColor}
                                    noWrap
                                >
                                    {experience.name
                                        ? capitalizeName(experience.name)
                                        : 'New Draft'}
                                    {experience.status === 'draft' && (
                                        <Chip
                                            label='Draft'
                                            size='small'
                                            sx={{ ml: 1 }}
                                        />
                                    )}
                                </Typography>
                                <Typography
                                    variant='body2'
                                    color={
                                        isUnpublished ? nameColor : detailColor
                                    }
                                    noWrap
                                    sx={{ mb: 2 }}
                                >
                                    {experience.streetAddress
                                        ? capitalizeName(
                                              experience.streetAddress,
                                          )
                                        : '—'}
                                </Typography>
                                <Typography
                                    variant='body3'
                                    color={
                                        isUnpublished ? detailColor : nameColor
                                    }
                                    noWrap
                                    sx={{ fontWeight: 700 }}
                                >
                                    {getListingSubType(experience)}
                                </Typography>
                                <Typography
                                    variant='body2'
                                    color={
                                        isUnpublished ? nameColor : detailColor
                                    }
                                    noWrap
                                >
                                    {formatHireRange(experience)}
                                </Typography>
                            </Stack>
                        </Stack>
                    );
                },
            },
            {
                id: 'location',
                header: 'Location',
                size: 240,
                grow: 1,
                accessorFn: (row) => getLocationLabel(row),
                Cell: ({ row }) => {
                    const isUnpublished = isUnpublishedListing(row.original);
                    const textColor = getRowTextColor(row.original);
                    const iconColor = isUnpublished
                        ? textColor
                        : 'text.secondary';

                    return (
                        <Stack
                            direction='row'
                            spacing={0.75}
                            alignItems='flex-start'
                        >
                            <PlaceOutlinedIcon
                                sx={{
                                    fontSize: 18,
                                    color: iconColor,
                                    mt: 0.25,
                                }}
                            />
                            <Typography
                                variant='body2'
                                color={
                                    isUnpublished ? textColor : 'text.primary'
                                }
                            >
                                {getLocationLabel(row.original) || '—'}
                            </Typography>
                        </Stack>
                    );
                },
            },
            {
                id: 'capacity',
                header: 'Capacity',
                size: 120,
                accessorFn: (row) => row?.details?.maxGuests ?? 0,
                Cell: ({ row }) => {
                    const isUnpublished = isUnpublishedListing(row.original);
                    const textColor = getRowTextColor(row.original);
                    const iconColor = isUnpublished
                        ? textColor
                        : 'text.secondary';

                    return (
                        <Stack
                            direction='row'
                            spacing={0.75}
                            alignItems='center'
                        >
                            <PeopleOutlineIcon
                                sx={{ fontSize: 18, color: iconColor }}
                            />
                            <Typography
                                variant='body2'
                                color={
                                    isUnpublished ? textColor : 'text.primary'
                                }
                            >
                                {row.original?.details?.maxGuests ?? '—'} guests
                            </Typography>
                        </Stack>
                    );
                },
            },
            {
                id: 'price',
                header: 'Price',
                size: 170,
                accessorFn: (row) => {
                    const { minPrice, billingExcluded, isFlexible } =
                        getPriceRange(row);
                    if (billingExcluded) return 0;
                    if (isFlexible) return -1;
                    return minPrice;
                },
                Cell: ({ row }) => {
                    const { minPrice, unitType, billingExcluded, isFlexible } =
                        getPriceRange(row.original);
                    const isUnpublished = isUnpublishedListing(row.original);
                    const textColor = getRowTextColor(row.original);
                    const mutedColor = isUnpublished
                        ? textColor
                        : 'text.secondary';

                    if (billingExcluded) {
                        return (
                            <Typography variant='body2' color={mutedColor}>
                                Free
                            </Typography>
                        );
                    }

                    if (isFlexible) {
                        return (
                            <Typography
                                variant='body2'
                                color={
                                    isUnpublished ? textColor : 'text.primary'
                                }
                            >
                                Flexible
                            </Typography>
                        );
                    }

                    if (!minPrice) {
                        return (
                            <Typography variant='body2' color={mutedColor}>
                                —
                            </Typography>
                        );
                    }

                    return (
                        <Typography
                            variant='body2'
                            color={isUnpublished ? textColor : 'text.primary'}
                        >
                            {formatCurrency(minPrice)}
                            {unitType ? ` / ${formatUnitType(unitType)}` : ''}
                        </Typography>
                    );
                },
            },
            {
                id: 'booked',
                header: 'Booked',
                size: 220,
                grow: 1,
                accessorFn: (row) =>
                    bookingByModuleId.get(row.id)?.startDate ?? '',
                Cell: ({ row }) => {
                    const booking = bookingByModuleId.get(row.original.id);
                    const isUnpublished = isUnpublishedListing(row.original);
                    const textColor = getRowTextColor(row.original);

                    return (
                        <Typography
                            variant='body2'
                            color={isUnpublished ? textColor : 'text.secondary'}
                        >
                            {booking
                                ? formatBookedRange(
                                      booking.startDate,
                                      booking.endDate,
                                  )
                                : '—'}
                        </Typography>
                    );
                },
            },
            {
                id: 'actions',
                header: 'Actions',
                size: 220,
                enableSorting: false,
                Cell: ({ row }) => {
                    const experience = row.original;
                    const isUnpublished = !experience.isAvailable;

                    return (
                        <Stack direction='row' spacing={1}>
                            <Button
                                type='button'
                                variant='contained'
                                size='small'
                                startIcon={<EditOutlinedIcon />}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleEdit(experience);
                                }}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: 1.5,
                                    px: 1.5,
                                    minWidth: 0,
                                }}
                            >
                                Edit
                            </Button>
                            {experience.status === 'draft' ? (
                                <Button
                                    type='button'
                                    variant='outlined'
                                    color='error'
                                    size='small'
                                    startIcon={<DeleteOutlinedIcon />}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setExperienceToDelete(experience);
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderRadius: 1.5,
                                        px: 1.5,
                                        minWidth: 0,
                                    }}
                                >
                                    Delete
                                </Button>
                            ) : (
                                <Button
                                    type='button'
                                    variant='outlined'
                                    size='small'
                                    startIcon={<VisibilityOffOutlinedIcon />}
                                    disabled={
                                        isUnpublished ||
                                        unpublishId === experience.id
                                    }
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleUnpublish(experience);
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderRadius: 1.5,
                                        px: 1.5,
                                        minWidth: 0,
                                    }}
                                >
                                    Unpublish
                                </Button>
                            )}
                        </Stack>
                    );
                },
            },
        ],
        [bookingByModuleId, handleEdit, unpublishId],
    );

    const formatPriceLabel = (experience: any) => {
        const { minPrice, unitType, billingExcluded, isFlexible } =
            getPriceRange(experience);

        if (billingExcluded) return 'Free';
        if (isFlexible) return 'Flexible';
        if (!minPrice) return '—';
        return `${formatCurrency(minPrice)}${
            unitType ? ` / ${formatUnitType(unitType)}` : ''
        }`;
    };

    if (isLoading || filteredAssets === undefined) {
        return <OwnerListingsTableLoadingSkeleton rowCount={5} />;
    }

    if (filteredAssets.length === 0) {
        return (
            <Stack
                alignItems='center'
                justifyContent='center'
                spacing={2}
                sx={{ py: 8, width: '100%' }}
            >
                <Box
                    component='img'
                    alt='No results'
                    src='/noResults.png'
                    sx={{ width: '100%', maxWidth: 420 }}
                />
                <Typography color='text.primary'>
                    Your query did not return any results
                </Typography>
            </Stack>
        );
    }

    if (isMobile) {
        return (
            <>
                <Stack spacing={2} sx={{ width: '100%', pb: 2 }}>
                    {filteredAssets.map((experience: any) => {
                        const booking = bookingByModuleId.get(experience.id);

                        return (
                            <OwnerListingMobileCard
                                key={experience.id}
                                experience={experience}
                                bookingLabel={
                                    booking
                                        ? formatBookedRange(
                                              booking.startDate,
                                              booking.endDate,
                                          )
                                        : '—'
                                }
                                capacityLabel={`${
                                    experience?.details?.maxGuests ?? '—'
                                } guests`}
                                hireRangeLabel={formatHireRange(experience)}
                                locationLabel={getLocationLabel(experience)}
                                priceLabel={formatPriceLabel(experience)}
                                subTypeLabel={getListingSubType(experience)}
                                isUnpublishing={unpublishId === experience.id}
                                onOpen={handleOpen}
                                onEdit={handleEdit}
                                onUnpublish={handleUnpublish}
                                onDelete={setExperienceToDelete}
                            />
                        );
                    })}
                </Stack>
                {experienceToDelete && (
                    <DeleteExperienceModal
                        open
                        experience={experienceToDelete}
                        onClose={() => setExperienceToDelete(null)}
                    />
                )}
            </>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <GenericTable
                columns={columns}
                data={filteredAssets}
                tableName='My Listings'
                loading={isFetching}
                enableGlobalFilter={false}
                enableRowSelection={false}
                hideTopToolbar
                onRowClick={handleOpen}
                pagination={{ pageSize: 10, pageIndex: 0 }}
                paginationItemLabel='listings'
                cellStyles={{ padding: '6px 10px' }}
                maxHeight='calc(100vh - 130px - 300px - var(--app-prompt-banner-height, 0px))'
            />
            {experienceToDelete && (
                <DeleteExperienceModal
                    open
                    experience={experienceToDelete}
                    onClose={() => setExperienceToDelete(null)}
                />
            )}
        </Box>
    );
}
